// Grid tile: circular avatar with exclusive-color border and name below.

import React from 'react';
import { useTheme } from '@/presentation/contexts/ThemeContext';
import { useResponsiveDesign } from '@/presentation/responsiveDesign/responsiveDesign';
import { CachedImage } from '@/presentation/components/common/CachedImage';
import { IdentityBadge } from '@/presentation/components/common/identity/IdentityBadge';
import { UserUtils } from '@/domain/utils/userUtils';
import { qaAttr, type QaAttrOptions } from '@/presentation/utils/qaAttr';

const NAME_LINE_HEIGHT = 1.2;
const NAME_MAX_LINES = 2;

export interface UserAvatarGridItemProps {
  userId: string;
  name: string;
  onClick?: (userId: string) => void;
  width?: number | string;
  height?: number | string;
  /**
   * Optional `data-qa-*` override for the row. When omitted, falls back to the
   * generic `user-grid-item-<userId>` id so existing call-sites keep working;
   * pass it to give a context-specific id (e.g. `subscription-<id>`). Test-only;
   * never affects rendering.
   */
  qa?: QaAttrOptions;
}

export const UserAvatarGridItem: React.FC<UserAvatarGridItemProps> = ({ userId, name, onClick, width, height, qa }) => {
  const { theme } = useTheme();
  const responsive = useResponsiveDesign();

  const isClickable = !!onClick;
  const fillCell = width === undefined && height === undefined;

  return (
    <div
      {...qaAttr(qa ?? { id: `user-grid-item-${userId}` })}
      onClick={isClickable ? () => onClick?.(userId) : undefined}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        width: '100%',
        paddingLeft: responsive.spacing['2xl'],
        paddingRight: responsive.spacing['2xl'],
        boxSizing: 'border-box',
        cursor: isClickable ? 'pointer' : 'default',
      }}
    >
      <CachedImage
        src={UserUtils.generateUserProfileImageUrl(userId)}
        alt={name}
        style={{
          width: fillCell ? '100%' : width,
          height: fillCell ? undefined : height,
          aspectRatio: fillCell ? '1 / 1' : undefined,
          borderRadius: responsive.borderRadius.round,
          border: `${responsive.border.thin}px solid ${theme.exclusive}`,
          objectFit: 'cover',
        }}
      />
      <div
        style={{
          marginTop: responsive.spacing.sm,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: responsive.spacing.xs,
          width: '100%',
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontSize: responsive.font.base,
            fontWeight: responsive.fontWeight.semibold,
            color: theme.text.primary,
            lineHeight: NAME_LINE_HEIGHT,
            height: `calc(${responsive.font.base} * ${NAME_LINE_HEIGHT * NAME_MAX_LINES})`,
            display: '-webkit-box',
            WebkitLineClamp: NAME_MAX_LINES,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            wordBreak: 'break-word',
            textAlign: 'center',
            minWidth: 0,
          }}
        >
          {name}
        </span>
        <IdentityBadge userId={userId} />
      </div>
    </div>
  );
};
